

// ========================
// HTTP HELPER (BULLETPROOF)
// ========================
const BASE = process.env.REACT_APP_API_BASE ? `${process.env.REACT_APP_API_BASE}/api` : '/api';

async function request(method, endpoint, data = null, options = {}) {
  const token = localStorage.getItem('radar_token');
  
  // Safer FormData detection (works across all browser contexts)
  const isFormData = data && typeof data.append === 'function';

  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  
  // Only add Content-Type if it is NOT a file upload
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const config = { method, headers, ...options };
  
  if (data) {
    config.body = isFormData ? data : JSON.stringify(data);
  }

  try {
    const res = await fetch(`${BASE}${endpoint}`, config);

    if (res.status === 401) {
      const json = await res.json().catch(() => ({}));
      if (json.code === 'TOKEN_EXPIRED') {
        const refreshed = await refreshToken();
        if (refreshed) return request(method, endpoint, data, options);
      }
      localStorage.removeItem('radar_token');
      localStorage.removeItem('radar_refresh_token');
      window.dispatchEvent(new Event('auth:logout'));
      
      console.error("🚨 ERREUR 401 BACKEND:", json); // Prints the exact backend reason
      throw new Error(json.error || 'Non authentifié (Token rejeté)');
    }

    if (!res.ok) {
      const json = await res.json().catch(() => ({ error: 'Erreur réseau' }));
      throw new Error(json.error || json.errors?.msg || 'Erreur serveur');
    }

    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('text/csv') || contentType.includes('application/octet-stream')) {
      return res.blob();
    }

    return res.json();
  } catch (err) {
    console.error("🚨 API REQUEST FAILED:", err.message);
    throw err;
  }
}

async function refreshToken() {
  try {
    const refreshTok = localStorage.getItem('radar_refresh_token');
    if (!refreshTok) return false;
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refreshTok })
    });
    if (!res.ok) return false;
    const { accessToken, refreshToken: newRefresh } = await res.json();
    localStorage.setItem('radar_token', accessToken);
    localStorage.setItem('radar_refresh_token', newRefresh);
    return true;
  } catch { return false; }
}

// ========================
// AUTH
// ========================
export const authApi = {
  login: (email, password) => request('POST', '/auth/login', { email, password }),
  register: (data) => request('POST', '/auth/register', data),
  adminCreateUser: (data) => request('POST', '/auth/admin/create-user', data),
  listUsers: () => request('GET', '/auth/admin/users'),
  deleteUser: (id) => request('DELETE', `/auth/admin/users/${id}`),
  logout: () => request('POST', '/auth/logout'),
  me: () => request('GET', '/auth/me'),
  updateProfile: (data) => request('PUT', '/auth/profile', data),
  changePassword: (currentPassword, newPassword) =>
    request('POST', '/auth/change-password', { currentPassword, newPassword }),
  refresh: () => request('POST', '/auth/refresh', {
    refreshToken: localStorage.getItem('radar_refresh_token')
  }),
  uploadAvatar: (formData) => request('POST', '/auth/avatar', formData) 
};

// ========================
// TERRITORIES
// ========================
function normalizeTerritory(t) {
  if (!t) return t;
  if (t.slug && !t.id) return { ...t, id: t.slug };
  return t;
}
function normalizeList(arr) { return Array.isArray(arr) ? arr.map(normalizeTerritory) : arr; }

export const territoriesApi = {
  list: () => request('GET', '/territories').then(normalizeList),
  get: (id) => request('GET', `/territories/${id}`).then(normalizeTerritory),
  update: (id, data) => request('PUT', `/territories/${id}`, data).then(normalizeTerritory),
  updateRules: (id, rules) => request('PUT', `/territories/${id}/rules`, { rules }),
  updateCheckpoints: (id, checkpoints) => request('PUT', `/territories/${id}/checkpoints`, { checkpoints }),
  create: (data) => request('POST', '/territories', data).then(normalizeTerritory),
  uploadPhotos: (id, formData) => request('POST', `/territories/${id}/gallery`, formData),
  deletePhoto: (id, imageId) => request('DELETE', `/territories/${id}/gallery/${imageId}`),
  uploadHero: (id, formData) => request('POST', `/territories/${id}/hero`, formData),
  delete: (id) => request('DELETE', `/territories/${id}`)
};

// ========================
// RANKINGS
// ========================
export const rankingsApi = {
  list: (territoryId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/rankings/${territoryId}${qs ? '?' + qs : ''}`);
  },
  live: (territoryId, edition) =>
    request('GET', `/rankings/${territoryId}/live${edition ? '?edition=' + edition : ''}`),
  add: (territoryId, data) => request('POST', `/rankings/${territoryId}`, data),
  recordCheckpoint: (territoryId, data) =>
    request('POST', `/rankings/${territoryId}/checkpoint`, data),
  recordFinish: (territoryId, data) =>
    request('POST', `/rankings/${territoryId}/finish`, data),
  delete: (territoryId, rankingId) =>
    request('DELETE', `/rankings/${territoryId}/${rankingId}`),
  exportCSV: async (territoryId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const blob = await request('GET', `/rankings/${territoryId}/export/csv${qs ? '?' + qs : ''}`);
    downloadBlob(blob, `classement-radar-${territoryId}.csv`);
  }
};

// ========================
// INSCRIPTIONS
// ========================
export const inscriptionsApi = {
  list: (territoryId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/inscriptions/${territoryId}${qs ? '?' + qs : ''}`);
  },
  create: (territoryId, data) => request('POST', `/inscriptions/${territoryId}`, data),
  updateStatus: (territoryId, id, status, dossardNumber) =>
    request('PATCH', `/inscriptions/${territoryId}/${id}/status`, { status, dossardNumber }),
  notifyAll: (territoryId, edition, customMessage) =>
    request('POST', `/inscriptions/${territoryId}/notify-all?edition=${edition}`, { customMessage }),
  exportCSV: async (territoryId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const blob = await request('GET', `/inscriptions/${territoryId}/export/csv${qs ? '?' + qs : ''}`);
    downloadBlob(blob, `inscriptions-radar-${territoryId}.csv`);
  }
};

// ========================
// FAQ
// ========================
export const faqApi = {
  list: () => request('GET', '/faq'),
  create: (data) => request('POST', '/faq', data),
  update: (id, data) => request('PUT', `/faq/${id}`, data),
  delete: (id) => request('DELETE', `/faq/${id}`),
  reorder: (order) => request('POST', '/faq/reorder', { order })
};

// ========================
// UTILITY
// ========================
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ========================
// NEWS & RESOURCES
// ========================
export const newsApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/news${qs ? '?' + qs : ''}`);
  },
  listAll: () => request('GET', '/news/all'),
  get: (id) => request('GET', `/news/${id}`),
  create: (formData) => request('POST', '/news', formData),
  update: (id, formData) => request('PUT', `/news/${id}`, formData),
  delete: (id) => request('DELETE', `/news/${id}`)
};

export const resourcesApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/resources${qs ? '?' + qs : ''}`);
  },
  listAll: () => request('GET', '/resources/all'),
  create: (formData) => request('POST', '/resources', formData),
  update: (id, formData) => request('PUT', `/resources/${id}`, formData),
  delete: (id) => request('DELETE', `/resources/${id}`),
  trackDownload: (id) => request('POST', `/resources/${id}/download`),
  reorder: (reorderedItems) => request('POST', '/resources/reorder', reorderedItems)
};

// ========================
// PARTNERS & EXTRAS
// ========================
export const partnersApi = {
  listGlobal: () => request('GET', '/partners'),
  listByTerritory: (slug) => request('GET', `/partners?territory=${slug}`),
  listAll: () => request('GET', '/partners/all'),
  create: (formData) => request('POST', '/partners', formData),
  update: (id, formData) => request('PUT', `/partners/${id}`, formData),
  delete: (id) => request('DELETE', `/partners/${id}`)
};

export const territoryExtrasApi = {
  updatePlaces: (slug, places) => request('PUT', `/territories/${slug}/places`, { places }),
};

export const announcementsApi = {
  list: () => request('GET', '/announcements'),
  create: (message) => request('POST', '/announcements', { message }),
  markRead: (id) => request('PATCH', `/announcements/${id}/read`, {}),
  delete: (id) => request('DELETE', `/announcements/${id}`)
};
// ========================
// TERRITORY AVATAR
// ========================
export const uploadTerritoryAvatar = (territoryId, formData) =>
  request('POST', `/territories/${territoryId}/avatar`, formData);